/** Campaign ABO page UI tokens — readable contrast, aligned with product reference. */

export const BID_V2_COLORS = {
  pageBg: "#F5F4FA",
  cardBg: "#FFFFFF",
  border: "#E2E4E9",
  borderFocus: "#6148f6",
  title: "#1C1C1E",
  body: "#334155",
  muted: "#64748B",
  label: "#475569",
  tableHeadBg: "#F7F6FE",
  tableHeadText: "#334155",
  infoBg: "#F3F0FF",
  infoBorder: "#C4B5FD",
  infoText: "#4C1D95",
  slotBg: "#FAFBFC",
  switchBarBg: "#F8FAFC",
};

export const sectionCardSx = {
  borderRadius: 3,
  border: `1px solid ${BID_V2_COLORS.border}`,
  bgcolor: BID_V2_COLORS.cardBg,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
};

export const sectionTitleSx = {
  fontSize: "1.0625rem",
  fontWeight: 700,
  color: BID_V2_COLORS.title,
  letterSpacing: "-0.01em",
  lineHeight: 1.3,
};

export const sectionSubtitleSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: BID_V2_COLORS.muted,
  lineHeight: 1.5,
};

export const sectionHeaderWrapSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  mb: 2.5,
};

export const sectionStepBadgeSx = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  flexShrink: 0,
  mt: 0.15,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.8125rem",
  fontWeight: 700,
  lineHeight: 1,
  color: "#fff",
  bgcolor: "primary.main",
  boxShadow: "0 2px 6px rgba(97, 72, 246, 0.28)",
};

export const fieldLabelSx = {
  display: "block",
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: BID_V2_COLORS.label,
  mb: 0.75,
};

export const tabSectionTitleSx = {
  ...sectionTitleSx,
  color: "primary.main",
  pb: 1,
  mb: 2,
  borderBottom: "2px solid",
  borderColor: "primary.main",
  display: "inline-block",
  width: "auto",
};

export const textFieldSx = {
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: BID_V2_COLORS.label,
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    fontSize: "0.9375rem",
    fontWeight: 500,
    color: BID_V2_COLORS.title,
    "& fieldset": {
      borderColor: BID_V2_COLORS.border,
    },
    "&:hover fieldset": {
      borderColor: "#94A3B8",
    },
    "&.Mui-focused fieldset": {
      borderColor: BID_V2_COLORS.borderFocus,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#94A3B8",
    opacity: 1,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    cursor: "not-allowed",
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

/** Prevents mouse-wheel / trackpad from changing focused number inputs while scrolling. */
export const numberFieldInputProps = {
  onWheel: (event) => event.currentTarget.blur(),
};

export const selectFieldSx = {
  ...textFieldSx,
  "& .MuiSelect-select": {
    fontSize: "0.9375rem",
    fontWeight: 500,
  },
};

export const tableHeadCellSx = {
  fontWeight: 700,
  fontSize: "0.8125rem",
  color: BID_V2_COLORS.tableHeadText,
  bgcolor: BID_V2_COLORS.tableHeadBg,
  py: 1.25,
  whiteSpace: "nowrap",
};

/** Campaign column left; all other table headers centered. */
export const tableHeaderAlign = (label) => (label === "Campaign" ? "left" : "center");

/** Campaign name left; all other row values centered (creation page tables). */
export const tableBodyAlign = (columnKey) => (columnKey === "Campaign" ? "left" : "center");

export const tableBodyCellSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: BID_V2_COLORS.body,
  py: 1.25,
};

export const statusPillSx = (active) => ({
  height: 32,
  px: 1.75,
  fontSize: "0.8125rem",
  fontWeight: 600,
  borderRadius: "999px",
  cursor: "pointer",
  transition: "all 0.15s ease",
  ...(active
    ? {
        bgcolor: "primary.main",
        color: "#fff",
        border: "1px solid",
        borderColor: "primary.main",
        boxShadow: "0 1px 2px rgba(97, 72, 246, 0.25)",
      }
    : {
        bgcolor: "#fff",
        color: BID_V2_COLORS.body,
        border: "1px solid",
        borderColor: BID_V2_COLORS.border,
        "&:hover": {
          bgcolor: "#F8FAFC",
          borderColor: "#94A3B8",
        },
      }),
});

export const dayCircleSx = (selected) => ({
  minWidth: 40,
  width: 40,
  height: 40,
  borderRadius: "50%",
  p: 0,
  fontSize: "0.875rem",
  fontWeight: 700,
  ...(selected
    ? {
        bgcolor: "primary.main",
        color: "#fff",
        boxShadow: "0 2px 4px rgba(97, 72, 246, 0.3)",
        "&:hover": { bgcolor: "primary.dark" },
      }
    : {
        bgcolor: "#fff",
        color: BID_V2_COLORS.body,
        border: "1px solid",
        borderColor: BID_V2_COLORS.border,
        "&:hover": { borderColor: "primary.main", color: "primary.main" },
      }),
});

export const durationLabelSx = {
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: BID_V2_COLORS.muted,
  textTransform: "uppercase",
};

export const slotPaperSx = {
  p: 2.5,
  my: 2,
  borderRadius: 2.5,
  border: "2px solid",
  borderColor: "primary.main",
};

export const infoAlertSx = {
  py: 1.25,
  px: 2,
  borderRadius: 2,
  bgcolor: BID_V2_COLORS.infoBg,
  color: BID_V2_COLORS.infoText,
  border: `1px solid ${BID_V2_COLORS.infoBorder}`,
  fontSize: "0.875rem",
  fontWeight: 500,
  "& .MuiAlert-icon": { color: "primary.main" },
};

export const radioLabelSx = {
  "& .MuiFormControlLabel-label": {
    fontSize: "0.9375rem",
    fontWeight: 500,
    color: BID_V2_COLORS.title,
  },
};

export const perfSwitchBarSx = {
  mt: 2.5,
  px: 2,
  py: 1.25,
  borderRadius: 2,
  bgcolor: BID_V2_COLORS.switchBarBg,
  border: `1px solid ${BID_V2_COLORS.border}`,
  "& .MuiFormControlLabel-label": {
    fontSize: "0.9375rem",
    fontWeight: 600,
    color: BID_V2_COLORS.body,
  },
};

export const nestedScheduleBoxSx = {
  mt: 2.5,
  p: 2.5,
  borderRadius: 2.5,
  bgcolor: "#fff",
  border: `1px solid ${BID_V2_COLORS.border}`,
};

export const slotActionIconSx = ({ active = false, disabled = false } = {}) => ({
  width: 44,
  height: 44,
  p: 1,
  color: disabled ? BID_V2_COLORS.muted : active ? "primary.main" : BID_V2_COLORS.body,
  "&:hover": {
    bgcolor: "action.hover",
  },
});

export const durationRowLayoutSx = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: 2,
  alignItems: { xs: "stretch", md: "center" },
};

export const durationFieldsGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(3, minmax(0, 1fr))",
  },
  gap: 2,
  flex: 1,
  minWidth: 0,
  alignItems: "center",
};

export const durationRowActionsSx = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: { xs: "flex-end", md: "flex-start" },
  gap: 0.5,
  flexShrink: 0,
};

export const collapseHeaderBtnSx = {
  mt: 2,
  justifyContent: "space-between",
  px: 2,
  py: 1.25,
  borderRadius: 2,
  bgcolor: "#fff",
  border: `1px solid ${BID_V2_COLORS.border}`,
  color: BID_V2_COLORS.title,
  fontWeight: 600,
  fontSize: "0.875rem",
  textTransform: "none",
  "&:hover": { bgcolor: "#F8FAFC", borderColor: "#94A3B8" },
};
